import { IAlbum } from 'ngx-lightbox';
import AssetHelper from 'src/app/helpers/asset-helper';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import ProjectHelper from 'src/app/helpers/project-helper';
import ProductAsset from 'src/app/model/product-asset';
import Employee from 'src/app/model/product/employee';
import Address from 'src/app/model/project/address';
import {
  SUB_PROJECT_TYPES,
  SubProject,
  TypeToDisplayAndClass,
} from 'src/app/model/project/sub-project';
import { clone } from 'lodash';
import Product from 'src/app/model/product/product';
import { ProductTypes } from 'src/app/model/product/product-type';
import { StringHelper } from 'src/app/helpers/string-helper';
import OtherProduct from 'src/app/model/product/other-product/other-product';
import { BoardTypeChoices } from 'src/app/model/product/board/board-part';

export class ClientCornerHomePageCompletedSubProjectsHelper {
  private constructor() {}

  static processSubProjects(
    subProjects: SubProject[]
  ): CompletedSubProjectItem[] {
    if (subProjects == null) {
      return null;
    }
    if (subProjects.length === 0) {
      return [];
    }
    const toReturn: CompletedSubProjectItem[] = [];
    subProjects.forEach((sp) => this.processSubProject(sp, toReturn));
    return toReturn;
  }

  private static processSubProject(
    subProject: SubProject,
    accumulator: CompletedSubProjectItem[]
  ): void {
    const type = TypeToDisplayAndClass.get(subProject.type);
    const subProjectAssets: SubProjectAssetObject =
      this.getSubProjectAssets(subProject);
    const toAdd: CompletedSubProjectItem = {
      id: subProject.id,
      projectId: subProject.project.id,
      oldPlatformId: subProject.project.oldPlatformId,
      type: subProject.type,
      typeClass: type.class,
      typeDisplay: type.display,
      executionDate: subProject.currentExecutionDate,
      address: subProject.projectPlacement.address,
      firstPlacer: ProjectHelper.getFirstPlacer(subProject),
      firstAsset: subProjectAssets == null ? null : subProjectAssets.firstAsset,
      lightboxAlbum:
        subProjectAssets == null ? null : subProjectAssets.lightboxAlbum,
      products: null,
    };
    if (subProject.type === SUB_PROJECT_TYPES.BASE_PROJECT) {
      this.handleBaseProjectProducts(subProject, toAdd, accumulator);
    } else {
      this.handleAdjustmentProducts(subProject, toAdd, accumulator);
    }
  }

  private static handleBaseProjectProducts(
    subProject: SubProject,
    item: CompletedSubProjectItem,
    accumulator: CompletedSubProjectItem[]
  ): void {
    if (CollectionHelper.isPopulated(subProject.project.products)) {
      subProject.project.products.forEach((p, i) => {
        item = i > 0 ? clone(item) : item;
        item.products = [this.getProductDisplay(p, true)];
        accumulator.push(item);
      });
    }
  }

  private static handleAdjustmentProducts(
    subProject: SubProject,
    display: CompletedSubProjectItem,
    accumulator: CompletedSubProjectItem[]
  ): void {
    display.products = CollectionHelper.isPopulated(subProject.project.products)
      ? subProject.project.products.map((p) =>
          this.getProductDisplay(p, subProject.type === SUB_PROJECT_TYPES.REPO)
        )
      : [];
    accumulator.push(display);
  }

  private static getProductDisplay(
    product: Product,
    isBaseProject: boolean
  ): CompletedSubProjectProduct {
    return {
      type: product.type,
      typeDisplay: this.getProductTypeDisplay(product),
      formatDisplay: product.overviewFormat,
      firstAsset:
        isBaseProject && CollectionHelper.isPopulated(product.productAssets)
          ? product.productAssets[0]
          : null,
      lightboxAlbum:
        isBaseProject && CollectionHelper.isPopulated(product.productAssets)
          ? product.productAssets.map((pa) => AssetHelper.toIAlbum(pa))
          : null,
    };
  }

  private static getProductTypeDisplay(product: Product): string {
    switch (product.type) {
      case ProductTypes.WINDOWSTICKER.value:
      case ProductTypes.CUSTOMWINDOWSTICKER.value:
        return 'Raamsticker';
      case ProductTypes.BOARD.value:
      case ProductTypes.IP_BOARD.value:
        if (
          product.boardPartBoardTypeDisplay === BoardTypeChoices.DIVERGENT.label
        ) {
          return StringHelper.join(
            [
              product.boardPartBoardTypeDisplay,
              product.boardPartDivergentBoardTypeDisplay,
            ],
            ': '
          );
        } else {
          return StringHelper.join(
            [
              product.boardPartBoardTypeDisplay,
              product.boardPartPlacementTypeDisplay,
            ],
            ' '
          );
        }
      case ProductTypes.OTHER_PRODUCT.value:
        const otherProduct = <OtherProduct>product;
        return StringHelper.isEmpty(otherProduct.otherProductName)
          ? 'Ander product'
          : otherProduct.otherProductName;
      default:
        throw new Error(`Unrecognized product type: ${product.type}`);
    }
  }

  private static getSubProjectAssets(
    subProject: SubProject
  ): SubProjectAssetObject {
    return subProject.type === SUB_PROJECT_TYPES.BASE_PROJECT ||
      subProject.projectPlacement == null ||
      !CollectionHelper.isPopulated(
        subProject.projectPlacement.projectPlacementAssets
      )
      ? null
      : {
          firstAsset: subProject.projectPlacement.projectPlacementAssets[0],
          lightboxAlbum: subProject.projectPlacement.projectPlacementAssets.map(
            (pa) => AssetHelper.toIAlbum(pa)
          ),
        };
  }
}

interface SubProjectAssetObject {
  firstAsset?: ProductAsset;
  lightboxAlbum?: IAlbum[];
}

export interface CompletedSubProjectItem {
  id: number;
  projectId: number;
  oldPlatformId: number;
  type: string;
  typeClass: string;
  typeDisplay: string;
  executionDate: Date;
  address: Address;
  firstPlacer: Employee;
  firstAsset?: ProductAsset;
  lightboxAlbum?: IAlbum[];
  products: CompletedSubProjectProduct[];
}

export interface CompletedSubProjectProduct {
  type: string;
  typeDisplay: string;
  formatDisplay: string;
  firstAsset?: ProductAsset;
  lightboxAlbum?: IAlbum[];
}
